
import React, { PropsWithChildren, ReactElement, useContext, useEffect } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './BankProductHeader.css';
import '../../component-library/molecules/Header/Header.css'
import { SideMenuContext } from '../../providers/SideMenuProvider/SideMenuProvider';
import { useWidthContext } from '../../providers/Width';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { retrieveDBOsgiUrl, safeParse } from 'utils/cob-utils';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import HeaderLogo from '../../component-library/molecules/Header/components/HeaderLogo';
import { Row, Col, Container, Button } from '../../importCL';

export type TCreateSideMenu = {
    title: string;
    link?: string;
    linkParams?: string;
    newTab?: boolean;
    next?: TCreateSideMenu | TCreateSideMenu[];
};
interface IBankProductHeaderProps extends MappedComponentProperties {
    logoVariant?: 'dark' | 'light' | 'transparent' | 'black-white' | string | any;
    logoUrl?: string;
    altText?: string;

    acctLabel?: string;
    acctId?: string;
    acctNewTab?: boolean;

    signInLabel?: string;
    signInLink?: string;
    signInId?: string;
    signInNewTab?: boolean;

    container?: TCreateSideMenu;
    homeLabel?: string;
    homeLink?: string;
}

export const BankProductHeaderEditConfig = {
    emptyLabel: 'Bank Product Header',
    isEmpty: (props: PropsWithChildren<IBankProductHeaderProps>) => {
        return !props || !props.logoUrl;
    },
};

const BankProductHeader = (props: PropsWithChildren<IBankProductHeaderProps>): JSX.Element => {
    const { hamburgerOpen, setHamburgerOpen, hamburgerSide, setHamburgerContainer } = useContext(SideMenuContext);
    const { width } = useWidthContext();

    const accountLinkUrl = retrieveDBOsgiUrl(safeParse(PathUtils.getMetaPropertyValue('digitalBankTerms'))?.baseUrl);

    useEffect(() => {
        if (!props.container)
            return;

        setHamburgerContainer({
            'title': props.homeLabel ?? '',
            'link': props.homeLink ?? '',
            'next': props.container,
        });
    }, [props.container]);

    return (<div id='bank-product-header' className='BankProductHeader py-4'>
        <Container>
            <Row className='BankProductHeader__Row'>
                {hamburgerSide === 'left' &&
                    <Col className='BankProductHeader__buttons left-side COL-3 COL-SM-3 COL-MD-10 COL-LG-10'>
                        <button className={`BankProductHeader__hamburger${hamburgerOpen ? '--active' : ''}`} aria-label='Open Sidebar Navigation' onClick={() => setHamburgerOpen(!hamburgerOpen)}>
                            <span className={`BankProductHeader__hamburgerIcon${hamburgerOpen ? '--active' : ''}`} />
                        </button>
                    </Col>
                }
                <HeaderLogo
                    centeredLogo={false}                            
                    theme={props.logoVariant ?? 'light'}
                    logoURL={props.logoUrl ?? '/'}
                    logoAlt={props.altText}
                    shouldRender
                    tag={undefined}
                    tagStyledAs={undefined}
                    navBarLinks={undefined}
                    logo={undefined}
                    textLogo={undefined}
                    customMessage={undefined}
                />
                <Col className='BankProductHeader__buttons COL-3 COL-SM-3 COL-MD-10 COL-LG-10'>
                    {props.acctLabel && accountLinkUrl && width > 768 &&
                        <Button size='small' variant='ghosted-dark' className='BankProductHeader__openAccount'
                            href={accountLinkUrl} target={props.acctNewTab ? '_blank' : '_self'} id={props.acctId}>
                            {transformHTMLParserLinks(wrapTrademarkSuperscript(props.acctLabel)) as unknown as ReactElement}
                        </Button>}
                    {props.signInLabel && props.signInLink &&
                        <div className='BankProductHeader__buttons--signIn'>
                            <Button size='small' className='mb-0 mr-4 [@media(min-width:769px)]:mr-6' variant='solid'
                                href={props.signInLink} target={props.signInNewTab ? '_blank' : '_self'} id={props.signInId}>
                                <svg className="'IconLock icon icon--lock" width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-label="Lock Icon">
                                    <path fill-rule="evenodd" clip-rule="evenodd"
                                        d="M7.85505 0.666505C10.146 0.664974 12.0194 2.36472 12.1031 4.48313L12.1061 4.6282L12.1078 6.63427H12.5394C13.3098 6.63427 13.9429 7.19042 13.9965 7.89624L14.0001 7.99338V13.9676C14.0017 14.6862 13.4039 15.2751 12.647 15.3249L12.5429 15.3283L3.46076 15.3332C2.69031 15.3332 2.05727 14.7756 2.00368 14.0696L2 13.9725V7.9999C1.9984 7.28131 2.59621 6.69243 3.35311 6.64115L3.45727 6.63753L3.90948 6.63427L3.90598 4.6282C3.90423 2.44286 5.51201 0.668198 7.85505 0.666505ZM7.99999 9.83323C7.87568 9.8332 7.75661 9.88197 7.66975 9.96849C7.60026 10.0377 7.55608 10.1264 7.54271 10.221L7.53854 10.2929V11.2016C7.53559 11.323 7.58273 11.4405 7.66943 11.5278C7.75613 11.6152 7.87518 11.6652 7.99999 11.6666C8.2372 11.6612 8.42987 11.4865 8.45796 11.2635L8.46145 11.2016V10.2929C8.46291 10.1724 8.41512 10.0563 8.32858 9.97007C8.24204 9.88387 8.12384 9.83464 7.99999 9.83323ZM5.43045 4.87886C5.47593 3.42708 6.27449 1.9999 7.9973 1.9999C9.77231 1.9999 10.5679 3.51312 10.5679 5.00734L10.5712 6.63753L5.43173 6.64106L5.42838 5.01087L5.43045 4.87886Z">
                                    </path>
                                </svg>
                                {transformHTMLParserLinks(wrapTrademarkSuperscript(props.signInLabel)) as unknown as ReactElement}                                
                            </Button>                            
                        </div>}
                    {hamburgerSide !== 'left' &&
                        <button
                            className={`BankProductHeader__hamburger${hamburgerOpen ? '--active' : ''}`}
                            aria-label='Open Sidebar Navigation'
                            onClick={() => setHamburgerOpen(!hamburgerOpen)}
                        >
                            <span className={`BankProductHeader__hamburgerIcon${hamburgerOpen ? '--active' : ''}`} />
                        </button>
                    }
                </Col>
            </Row>
        </Container>
    </div>);
};

export default BankProductHeader;
